import { type PropsWithChildren, Suspense, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import {
  enableSimplifiedFlagCreation,
  enableViews,
  flagListDisplayPreferences,
} from '@gonfalon/dogfood-flags/dogfood-flags';
import { QuietErrorBoundary } from '@gonfalon/error-boundaries/QuietErrorBoundary';
import { matchFlagCreation } from '@gonfalon/navigator/matchers';
import { useSlots } from '@gonfalon/react/useSlots';
import clsx from 'clsx';
import invariant from 'tiny-invariant';

import { Action } from '../internal/Action';
import { ChangeHistoryTrigger } from '../internal/ChangeHistoryTrigger';
import { DisplayPreferences } from '../internal/DisplayPreferences';
import { EnvironmentController } from '../internal/EnvironmentController';
import { Filters } from '../internal/Filters';
import { LayoutHeader, LayoutHeaderActions } from '../internal/LayoutHeader';
import { Ordering } from '../internal/Ordering';
import { ShortcutsTrigger } from '../internal/ShortcutsTrigger';
import { useInlineChangeHistory } from '../internal/useInlineChangeHistory';
import { useMaxWidthAndCenteredLayoutContent } from '../internal/useMaxWidthAndCenteredLayoutContent';
import { Views } from '../internal/Views';
import { ViewsFilter } from '../internal/ViewsFilter';

import styles from './ProjectCollectionLayout.module.css';

export function ProjectCollectionLayout({ children }: PropsWithChildren) {
  const location = useLocation();

  const [{ context, content, banner, footer }] = useSlots(children, {
    content: ProjectCollectionContent,
    footer: ProjectCollectionFooter,
    banner: ProjectCollectionBanner,
    context: ProjectCollectionContext,
  });

  invariant(content, 'ProjectCollectionLayout must include a ProjectCollectionContent');

  const isMaxWidthAndCenteredLayoutContent = useMaxWidthAndCenteredLayoutContent();
  const hasInlineChangeHistory = useInlineChangeHistory();

  // this is temporary to support the new flag creation modal above the flags list experience
  // while keeping the old full page experience for the flag creation route working
  const isFlagCreationRoute = matchFlagCreation(location);
  const hideHeaderActionsIfFullScreenFlagCreation = !enableSimplifiedFlagCreation() && isFlagCreationRoute;

  return (
    <div className={styles.collection}>
      <LayoutHeader isMaxWidthAndCentered={isMaxWidthAndCenteredLayoutContent}>
        <LayoutHeaderActions>
          {hideHeaderActionsIfFullScreenFlagCreation ? null : (
            <>
              {enableViews() ? <ViewsFilter /> : null}
              <Filters />
              <Ordering />
              <Views />
              {flagListDisplayPreferences() ? <DisplayPreferences /> : null}
              <Action />
              <Suspense fallback={null}>
                <QuietErrorBoundary>
                  <ShortcutsTrigger />
                </QuietErrorBoundary>
              </Suspense>
              {hasInlineChangeHistory ? null : <ChangeHistoryTrigger />}
            </>
          )}
        </LayoutHeaderActions>
      </LayoutHeader>
      {banner}
      <div className={styles.scroller} data-scroller>
        {context}
        <div className={clsx(styles.main, { [styles.maxWidthAndCentered]: isMaxWidthAndCenteredLayoutContent })}>
          {content}
        </div>
      </div>
      {footer}
    </div>
  );
}

export function ProjectCollectionContext({ children, wide = false }: PropsWithChildren<{ wide?: boolean }>) {
  const isMaxWidthAndCentered = useMaxWidthAndCenteredLayoutContent();
  const [{ freeform, controls, environmentSelector }] = useSlots(children, {
    freeform: ProjectCollectionFreeform,
    controls: ProjectCollectionControls,
    environmentSelector: EnvironmentController,
  });
  const contextRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const context = contextRef.current;
    if (!context || !environmentSelector) {
      return;
    }

    const scroller = context.closest('[data-scroller]');
    if (!scroller) {
      return;
    }

    const handleScroll = () => {
      const { scrollLeft, clientWidth, scrollWidth } = scroller;
      context.toggleAttribute('data-scrolled', scrollLeft > 0);
      context.toggleAttribute('data-scrolled-right', scrollLeft + clientWidth < scrollWidth - 1);
    };

    scroller.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => scroller.removeEventListener('scroll', handleScroll);
  }, [environmentSelector]);

  if (freeform) {
    return (
      <div className={clsx(styles.context, { [styles.maxWidthAndCentered]: isMaxWidthAndCentered })}>{freeform}</div>
    );
  }

  return (
    <div
      ref={contextRef}
      className={clsx(styles.context, {
        [styles.contextWide]: wide,
        [styles.maxWidthAndCentered]: isMaxWidthAndCentered,
      })}
      data-context
    >
      {controls}
      {environmentSelector && <div>{environmentSelector}</div>}
    </div>
  );
}

export function ProjectCollectionFreeform({ children }: PropsWithChildren) {
  return <div className={styles.freeform}>{children}</div>;
}

export function ProjectCollectionContent({ children }: PropsWithChildren) {
  return <div className={styles.content}>{children}</div>;
}

export function ProjectCollectionBanner({ children }: PropsWithChildren) {
  return <div className={styles.banner}>{children}</div>;
}

export function ProjectCollectionFooter({ children }: PropsWithChildren) {
  return <div className={styles.footer}>{children}</div>;
}

export function ProjectCollectionControls({ children }: PropsWithChildren) {
  return <div className={styles.controls}>{children}</div>;
}
